import React, { useState } from 'react'
import { YStack, XStack, Text, Icon, Button, Slider, styled, useDebounceValue, ViewProps } from '@red-ui/components'
import { getString } from '@vrw/data'
import { formatPoints } from '@vrw/data/src/utils/formatters'
import { useAppSelector } from '@/redux/hooks/hooks'
import { getBalance } from '@vrw/data/src/redux/balance/balance.selectors'
import { RedemptionType } from '@vrw/data/src/redux/rewards/types'
import { useFeatureSelector } from '@vrw/data/src/redux/features/features.selectors'
import { FeatureName } from '@vrw/data/src/redux/features/features.types'
import { useA11yLiveAnnouncer } from '@/context/A11yLiveAnnouncer'

const IncrementalButton = styled(Button, {
  width: '$4',
  height: '$4',
  backgroundColor: '$white',
  borderColor: '$black3',
  hoverStyle: {
    backgroundColor: '$black1',
    borderColor: '$black3',
    cursor: 'pointer',
  },
  disabledStyle: {
    backgroundColor: '$black1',
    cursor: 'auto',
  },
  pressStyle: {
    backgroundColor: '$black3',
    borderColor: '$black3',
  },
})

interface PointsCardProps extends ViewProps {
  title: string
  points: number
  balance: number
}

const PointsCard = ({ title, points, balance, ...props }: PointsCardProps) => (
  <YStack flexBasis={0} flexGrow={1} alignItems="center" padding="$2" borderRadius="$4" borderWidth={1} borderColor="$red6" {...props}>
    <Text fontWeight="500">{title}</Text>
    <Text fontSize="$7" fontWeight="600" textAlign="center">
      {points > 0 ? '-' : ''}
      {formatPoints({ points })} <Text fontSize="$5">{getString('misc.points.short')}</Text>
    </Text>
    <Text $mobile={{ fontSize: '$2' }} textAlign="center">
      {`${getString('pointsPicker.balance')} ${formatPoints({ points: balance })} ${getString('misc.points.short')}`}
    </Text>
  </YStack>
)

export type PointsToRedeem = {
  virgin: number
  bilt: number
}

export type PointsRedemptionSliderProps = {
  cost: number
  pointsToRedeem: PointsToRedeem
  onChange: (points: PointsToRedeem) => void
}

export const PointsRedemptionSlider = ({ cost, pointsToRedeem, onChange }: PointsRedemptionSliderProps) => {
  const virginPointsBalance = useAppSelector(getBalance)
  const biltPointsBalance = 5000 // need to fetch from bilt api
  const debouncedPointsToRedeem = useDebounceValue(pointsToRedeem, 25)
  const maxSliderValue = Math.min(biltPointsBalance, cost)
  const { announce } = useA11yLiveAnnouncer()

  const onValueChange = (value: number) => {
    onChange({
      virgin: cost - value,
      bilt: value,
    })
    announce(`${getString('account.linkAccounts.bilt.partnerName')} ${formatPoints({ points: value })} ${getString('misc.points.short')}`)
  }

  return (
    <YStack flex={1}>
      <XStack alignItems="center" gap="$1">
        <PointsCard
          title={getString('pointsPicker.bilt')}
          points={debouncedPointsToRedeem.bilt}
          balance={biltPointsBalance}
          backgroundColor="$white"
        />
        <YStack
          zIndex="$1"
          marginLeft="$-2"
          position="absolute"
          width="$4"
          height="$4"
          left="50%"
          alignItems="center"
          justifyContent="center"
          backgroundColor="$red6"
          borderRadius="$8">
          <Icon.Plus color="$white" />
        </YStack>
        <PointsCard
          title={getString('pointsPicker.virgin')}
          points={debouncedPointsToRedeem.virgin}
          balance={virginPointsBalance.usable ?? 0}
          backgroundColor="$red1"
        />
      </XStack>
      <XStack marginTop="$8">
        <Slider
          defaultValue={[0]}
          value={[pointsToRedeem.bilt]}
          onValueChange={(values) => onValueChange(values[0])}
          max={maxSliderValue}
          step={1}
          flex={1}>
          <Slider.Track height={2} backgroundColor="$red1">
            <Slider.TrackActive backgroundColor="$red6" hoverStyle={{ backgroundColor: '$red6' }} />
          </Slider.Track>
          <Slider.Thumb backgroundColor="$red6" borderColor="$red6" size="$2" index={0} circular>
            <Text userSelect="none" fontSize="$3" fontStyle="italic" fontWeight="500" top="$-4" left="$-4" position="absolute" width={100}>
              {`${getString('account.linkAccounts.bilt.partnerName')} ${formatPoints({ points: debouncedPointsToRedeem.bilt })} ${getString(
                'misc.points.short'
              )}`}
            </Text>
          </Slider.Thumb>
        </Slider>
      </XStack>
      <XStack marginTop="$2" flex={1} justifyContent="space-between">
        <XStack>
          <IncrementalButton
            aria-label={getString('pointsPicker.minus')}
            disabled={debouncedPointsToRedeem.bilt === 0}
            icon={<Icon.Minus color="$black10" aria-hidden />}
            onPress={() => onValueChange(debouncedPointsToRedeem.bilt - 1)}
          />
          <YStack marginLeft="$1" aria-labelledby={getString('pointsPicker.minus')}>
            <Text fontSize="$2">{getString('pointsPicker.min')}</Text>
            <Text fontStyle="italic" fontSize="$3" fontWeight="500">
              {formatPoints({ points: 0 })} {getString('misc.points.short')}
            </Text>
          </YStack>
        </XStack>

        <XStack>
          <YStack alignItems="flex-end" marginRight="$1" aria-labelledby={getString('pointsPicker.plus')}>
            <Text fontSize="$2">{getString('pointsPicker.max')}</Text>
            <Text fontStyle="italic" fontSize="$3" fontWeight="500">
              {formatPoints({ points: maxSliderValue })} {getString('misc.points.short')}
            </Text>
          </YStack>
          <IncrementalButton
            aria-label={getString('pointsPicker.plus')}
            disabled={debouncedPointsToRedeem.bilt === maxSliderValue}
            icon={<Icon.Plus color="$black10" aria-hidden />}
            onPress={() => onValueChange(debouncedPointsToRedeem.bilt + 1)}
          />
        </XStack>
      </XStack>
    </YStack>
  )
}

function getTotalPointsLabel(points: PointsToRedeem) {
  const pointSources: (keyof PointsToRedeem)[] = ['bilt', 'virgin']

  return pointSources
    .reduce<string[]>((acc, source) => {
      const value = points[source]

      if (value && value > 0) {
        const formatted = getString(`orders.checkout.${source}.points`).replace('{{points}}', formatPoints({ points: value }))
        acc.push(`${formatted} ${getString('misc.points.short')}`)
      }

      return acc
    }, [])
    .join(' + ')
}

export function usePointsRedemption(initialCost: number) {
  const [pointsToRedeem, setPointsToRedeem] = useState<PointsToRedeem>({
    virgin: initialCost,
    bilt: 0,
  })

  return {
    pointsToRedeem,
    setPointsToRedeem,
    totalPointsLabel: getTotalPointsLabel(pointsToRedeem),
  }
}

const SUPPORTED_REDEMPTION_TYPES = [
  RedemptionType.DIGITAL_FULFILMENT,
  RedemptionType.QR_CODE,
  RedemptionType.BARCODE_128,
  RedemptionType.WINES_VOUCHER,
  RedemptionType.VOUCHER_PLUS_URL,
  RedemptionType.DONATION_LINK,
]

export function useBiltSupported(redemptionType?: RedemptionType) {
  const webBiltFlag = useAppSelector(useFeatureSelector(FeatureName.WEB_BILT))

  return Boolean(webBiltFlag && redemptionType && SUPPORTED_REDEMPTION_TYPES.includes(redemptionType))
}
